import React from 'react';
import { Typography, Paper, Grid } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

type SalesByDay = {
  date: string;
  sales: number;
};

type StatisticsOrdersProps = {
  salesByDay: SalesByDay[];
};

export const ChartStatistics: React.FC<StatisticsOrdersProps> = ({ salesByDay }) => {
  const formatCurrency = (value: number) => `$${value?.toFixed(2)}`;
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd MMM', { locale: es });
    } catch {
      return dateString;
    }
  };

  const chartData = salesByDay?.map(day => ({
    ...day,
    formattedDate: formatDate(day.date)
  }));

  const totalSales = salesByDay?.reduce((sum, day) => sum + day.sales, 0) || 0;

  return (
    <Paper
      sx={{
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        color: '#000',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', textAlign: 'center' }}>
        Análisis de Ventas por Día
      </Typography>

      <Grid container spacing={3} alignItems="stretch" justifyContent="center">
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: '500', marginBottom: '12px', textAlign: 'center' }}>
            Ventas Diarias
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                formatter={(value: number) => [formatCurrency(value), 'Ventas']}
                labelFormatter={(label) => `Fecha: ${label}`}
              />
              <Bar dataKey="sales" fill="#1976D2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ marginTop: '16px' }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              padding: '16px',
              textAlign: 'center',
              backgroundColor: '#E3F2FD',
              borderRadius: '8px'
            }}>
            <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: '600' }}>
              {formatCurrency(totalSales)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total de Ventas en el Período
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};
